import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAcademySlug } from '../../../../hooks/useAcademySlug';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { usePublicBookingStore } from '../../../../store/publicBooking';
import { needsPublicBookingSessionPicker } from '../publicBookingSessionMode';
import { ClinicSessionPickerStep } from '../ClinicSessionPickerStep';
import { appendPublicBookingWizardContextQueries, publicBookingStepPath } from '../bookingPaths';

/** Camp daily booking: pick one or more camp days. Group drop-ins skip this step. */
export function BookingSessionsStep() {
  const academySlug = useAcademySlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const academy = usePublicBookingStore((s) => s.academy);
  const { formatDate } = useFormatDate({ region: academy?.region, publicContext: true });
  const lesson = usePublicBookingStore((s) => s.lesson);
  const term = usePublicBookingStore((s) => s.term);
  const userType = usePublicBookingStore((s) => s.userType);
  const invitationToken = usePublicBookingStore((s) => s.invitationToken);
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const selectedSessionDates = usePublicBookingStore((s) => s.selectedSessionDates);
  const setSelectedSessionDates = usePublicBookingStore((s) => s.setSelectedSessionDates);
  const campBookingMode = usePublicBookingStore((s) => s.campBookingMode);
  const groupBookingMode = usePublicBookingStore((s) => s.groupBookingMode);

  React.useEffect(() => {
    if (!userType) {
      navigate(
        appendPublicBookingWizardContextQueries(
          publicBookingStepPath(academySlug!, lessonId!, 'register'),
          { invitationToken, manualInviteToken }
        ),
        { replace: true }
      );
    }
  }, [userType, academySlug, lessonId, navigate, invitationToken, manualInviteToken]);

  if (!lesson || !academySlug || !lessonId) {
    return null;
  }

  const needsSessionPicker = needsPublicBookingSessionPicker(
    lesson,
    campBookingMode,
    groupBookingMode
  );
  if (!needsSessionPicker) {
    navigate(
      appendPublicBookingWizardContextQueries(
        publicBookingStepPath(academySlug, lessonId, 'details'),
        { invitationToken, manualInviteToken }
      ),
      { replace: true }
    );
    return null;
  }

  const hasCampDateRange =
    lesson.lessonType === 'camp' && lesson.startDate && lesson.endDate;
  if (!term && !hasCampDateRange) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Could not load session dates. Please go back and try again, or contact the academy.</p>
      </div>
    );
  }

  return (
    <ClinicSessionPickerStep
      lesson={lesson}
      term={term}
      formatDate={formatDate}
      initialSelectedDates={selectedSessionDates}
      onSelect={(dates) => {
        setSelectedSessionDates(dates);
        if (userType === 'new') {
          navigate(
            appendPublicBookingWizardContextQueries(
              publicBookingStepPath(academySlug, lessonId, 'signup'),
              { invitationToken, manualInviteToken }
            )
          );
        } else {
          navigate(
            appendPublicBookingWizardContextQueries(
              publicBookingStepPath(academySlug, lessonId, 'details'),
              { invitationToken, manualInviteToken }
            )
          );
        }
      }}
      onBack={() =>
        navigate(
          appendPublicBookingWizardContextQueries(
            publicBookingStepPath(academySlug, lessonId, 'register'),
            { invitationToken, manualInviteToken }
          )
        )
      }
    />
  );
}
